import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, X, AlertTriangle, Shield, Calendar } from 'lucide-react';
import AttachmentViewer from '../common/AttachmentViewer'
import './DisciplinaryModule.css';

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api';

const DisciplinaryModule = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    personnelName: '',
    incidentDate: '',
    reportedBy: '',
    incidentType: 'Absenteeism',
    severity: 'Minor',
    description: '',
    witnesses: '',
    investigationDate: '',
    investigatedBy: '',
    findings: '',
    personnelResponse: '',
    actionTaken: 'None',
    actionDate: '',
    suspensionDays: 0,
    effectiveDate: '',
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: '',
    status: 'Open'
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/disciplinary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data.data || []);
    } catch (err) {
      console.error('Error fetching disciplinary records:', err);
    } finally {
      setLoading(false);
    }
  };

     const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const data = new FormData();
      data.append('personnelName', formData.personnelName || '');
      data.append('incidentDate', formData.incidentDate || '');
      data.append('reportedBy', formData.reportedBy || '');
      data.append('incidentType', formData.incidentType || 'Absenteeism');
      data.append('severity', formData.severity || 'Minor');
      data.append('description', formData.description || '');
      data.append('status', formData.status || 'Pending');
      data.append('actionTaken', formData.actionTaken || 'None');
      data.append('actionDate', formData.actionDate || '');
      data.append('suspensionDays', formData.suspensionDays || 0);
      data.append('effectiveDate', formData.effectiveDate || '');
      data.append('investigationDate', formData.investigationDate || '');
      data.append('investigatedBy', formData.investigatedBy || '');
      data.append('findings', formData.findings || '');
      data.append('personnelResponse', formData.personnelResponse || '');
      
      // Handle witnesses array
      const witnessesArray = formData.witnesses.split(',').map(w => w.trim()).filter(w => w);
      data.append('witnesses', JSON.stringify(witnessesArray));
      
      // Attachment MUST be last for multer to parse text fields first
            if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach(file => data.append('attachments', file));
      }
      
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`
          // NO Content-Type header - axios sets it automatically with boundary for FormData
        }
      };
      
      if (selectedRecord) {
        await axios.put(`${API_URL}/disciplinary/${selectedRecord._id}`, data, config);
      } else {
        await axios.post(`${API_URL}/disciplinary`, data, config);
      }
      setShowModal(false);
           setSelectedRecord(null);
      resetForm();
      fetchRecords();
    } catch (err) {
      console.error('Error saving record:', err);
      alert(err.response?.data?.message || 'Failed to save record');
    }
  };

  

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this disciplinary record?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/disciplinary/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRecords();
    } catch (err) {
      console.error('Error deleting record:', err);
      alert('Failed to delete record');
    }
  };

  const resetForm = () => {
    setFormData({
      personnelName: '',
      incidentDate: '',
      reportedBy: '',
      incidentType: 'Absenteeism',
      severity: 'Minor',
      description: '',
      witnesses: '',
      investigationDate: '',
      investigatedBy: '',
      findings: '',
      personnelResponse: '',
      actionTaken: 'None',
      actionDate: '',
      suspensionDays: 0,
      effectiveDate: '',
      followUpRequired: false,
      followUpDate: '',
      followUpNotes: '',
      status: 'Open'
    });
  };

  const openModal = (record = null) => {
    if (record) {
      setSelectedRecord(record);
      setFormData({
       personnelName: record.personnelName || "",
        incidentDate: record.incidentDate ? record.incidentDate.split('T')[0] : '',
        reportedBy: record.reportedBy,
        incidentType: record.incidentType,
        severity: record.severity,
        description: record.description,
        witnesses: record.witnesses?.join(', ') || '',
        investigationDate: record.investigationDate ? record.investigationDate.split('T')[0] : '',
        investigatedBy: record.investigatedBy || '',
        findings: record.findings || '',
        personnelResponse: record.personnelResponse || '',
        actionTaken: record.actionTaken,
        actionDate: record.actionDate ? record.actionDate.split('T')[0] : '',
        suspensionDays: record.suspensionDays || 0,
        effectiveDate: record.effectiveDate ? record.effectiveDate.split('T')[0] : '',
        followUpRequired: record.followUpRequired,
        followUpDate: record.followUpDate ? record.followUpDate.split('T')[0] : '',
        followUpNotes: record.followUpNotes || '',
        status: record.status
      });
    } else {
      setSelectedRecord(null);
      resetForm();
    }
    setShowModal(true);
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'Minor': return '#4caf50';
      case 'Moderate': return '#ff9800';
      case 'Major': return '#ff5722';
      case 'Critical': return '#f44336';
      case 'Gross': return '#8b0000';
      default: return '#9e9e9e';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Open': return '#2196f3';
      case 'Under Investigation': return '#ff9800';
      case 'Resolved': return '#4caf50';
      case 'Appealed': return '#9c27b0';
      case 'Closed': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  const filteredRecords = records.filter(r => 
    String(r.personnelName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(r.personnelName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.incidentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="disciplinary-module">
      <div className="section-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search disciplinary records..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={18} />
          Add Disciplinary Record
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Personnel</th>
                <th>Incident Date</th>
                <th>Type</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Action Taken</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">No disciplinary records found</td>
                </tr>
              ) : (
                filteredRecords.map(record => (
                  <tr key={record._id}>
                    <td>
                      <div className="personnel-info">
                        <strong>{record.personnelName || "N/A"}</strong>
                        <span className="text-muted">Personnel</span>
                      </div>
                                       {record.attachments && record.attachments.map((url, idx) => (
                      <a key={idx} href={url} target="_blank">File {idx + 1}</a>
                    ))}                  
                    </td>
                    <td>{new Date(record.incidentDate).toLocaleDateString()}</td>
                    <td>{record.incidentType}</td>
                    <td>
                      <span className="severity-badge" style={{ backgroundColor: getSeverityColor(record.severity) }}>
                        {record.severity}
                      </span>
                    </td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: getStatusColor(record.status) }}>
                        {record.status}
                      </span>
                    </td>
                    <td>{record.actionTaken}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={() => openModal(record)} title="Edit">
                          <Shield size={16} />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(record._id)} title="Delete">
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-content modal-lg">
            <div className="modal-header">
              <h3>{selectedRecord ? 'Edit' : 'Add'} Disciplinary Record</h3>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Personnel Name or ID</label>
                  <input 
                    type="text" 
                    value={formData.personnelName}
                    onChange={(e) => setFormData({...formData, personnelName: e.target.value})}
                    required
                    placeholder="Enter personnel name or ID"
                  />
                </div>
                <div className="form-group">
                  <label>Incident Date *</label>
                  <input 
                    type="date" 
                    value={formData.incidentDate}
                    onChange={(e) => setFormData({...formData, incidentDate: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Reported By *</label>
                  <input 
                    type="text" 
                    value={formData.reportedBy}
                    onChange={(e) => setFormData({...formData, reportedBy: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Incident Type *</label>
                  <select 
                    value={formData.incidentType}
                    onChange={(e) => setFormData({...formData, incidentType: e.target.value})}
                    required
                  >
                    <option value="Absenteeism">Absenteeism</option>
                    <option value="Tardiness">Tardiness</option>
                    <option value="Misconduct">Misconduct</option>
                    <option value="Insubordination">Insubordination</option>
                    <option value="Theft">Theft</option>
                    <option value="Fighting">Fighting</option>
                    <option value="Damage to Property">Damage to Property</option>
                    <option value="Violation of Policy">Violation of Policy</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Severity *</label>
                  <select 
                    value={formData.severity}
                    onChange={(e) => setFormData({...formData, severity: e.target.value})}
                    required
                  >
                    <option value="Minor">Minor</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Major">Major</option>
                    <option value="Critical">Critical</option>
                    <option value="Gross">Gross</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select 
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="Open">Open</option>
                    <option value="Under Investigation">Under Investigation</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Appealed">Appealed</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="form-group form-group-full">
                <label>Description *</label>
                <textarea 
                  rows="4"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  placeholder="Detailed description of the incident..."
                />
              </div>
              <div className="form-group">
  <label>Attachment (Document)</label>
   <input 
    type="file" 
    multiple
    name="attachments" 
    onChange={(e) => setFormData(prev => ({ ...prev, attachments: Array.from(e.target.files) }))}
  />
</div>

              <div className="form-group form-group-full">
                <label>Witnesses (comma-separated)</label>
                <input 
                  type="text" 
                  value={formData.witnesses}
                  onChange={(e) => setFormData({...formData, witnesses: e.target.value})}
                  placeholder="John Doe, Jane Smith"
                />
              </div>

              <h4 className="section-title">Investigation</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Investigation Date</label>
                  <input 
                    type="date" 
                    value={formData.investigationDate}
                    onChange={(e) => setFormData({...formData, investigationDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Investigated By</label>
                  <input 
                    type="text" 
                    value={formData.investigatedBy}
                    onChange={(e) => setFormData({...formData, investigatedBy: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group form-group-full">
                <label>Findings</label>
                <textarea 
                  rows="3"
                  value={formData.findings}
                  onChange={(e) => setFormData({...formData, findings: e.target.value})}
                />
              </div>
              <div className="form-group form-group-full">
                <label>Personnel Response</label>
                <textarea 
                  rows="3"
                  value={formData.personnelResponse}
                  onChange={(e) => setFormData({...formData, personnelResponse: e.target.value})}
                />
              </div>

              <h4 className="section-title">Action Taken</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Action Taken</label>
                  <select 
                    value={formData.actionTaken}
                    onChange={(e) => setFormData({...formData, actionTaken: e.target.value})}
                  >
                    <option value="None">None</option>
                    <option value="Verbal Warning">Verbal Warning</option>
                    <option value="Written Warning">Written Warning</option>
                    <option value="Final Warning">Final Warning</option>
                    <option value="Suspension">Suspension</option>
                    <option value="Demotion">Demotion</option>
                    <option value="Termination">Termination</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Action Date</label>
                  <input 
                    type="date" 
                    value={formData.actionDate}
                    onChange={(e) => setFormData({...formData, actionDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Suspension Days</label>
                  <input 
                    type="number" 
                    min="0"
                    value={formData.suspensionDays}
                    onChange={(e) => setFormData({...formData, suspensionDays: parseInt(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>Effective Date</label>
                  <input 
                    type="date" 
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({...formData, effectiveDate: e.target.value})}
                  />
                </div>
              </div>

              <h4 className="section-title">Follow-up</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Follow-up Required</label>
                  <input 
                    type="checkbox" 
                    checked={formData.followUpRequired}
                    onChange={(e) => setFormData({...formData, followUpRequired: e.target.checked})}
                  />
                </div>
                <div className="form-group">
                  <label>Follow-up Date</label>
                  <input 
                    type="date" 
                    value={formData.followUpDate}
                    onChange={(e) => setFormData({...formData, followUpDate: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group form-group-full">
                <label>Follow-up Notes</label>
                <textarea 
                  rows="3"
                  value={formData.followUpNotes}
                  onChange={(e) => setFormData({...formData, followUpNotes: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                              <div className="form-group">
                <label>Created By</label>
                <input
                  type="text"
                  name="createdBy"
                  value={formData.createdBy || ''}
                  onChange={(e) => setFormData({...formData, createdBy: e.target.value})}
                  placeholder="Enter officer name"
                  className="form-control"
                />
              </div>
                <button type="submit" className="btn btn-primary">
                  {selectedRecord ? 'Update' : 'Save'} Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisciplinaryModule;
